import axios from 'axios';
import { base64Encode } from './basicUtilities';
import { generateGuid } from './randomUtilities';
import { requestWithRetry } from './requestRetryUtilities';

// The block size affects the blob uploading and downloading.
// The large block will cause the longer upload process and faster download process.
// The small block will be vice versa.
// Need to find a good middle, it's 20 megabytes as to me.
// The value is represented in bytes.
// 10 MB = 10485760 Bytes (in binary)
// 20 MB = 20971520 Bytes (in binary)
// 30 MB = 31457280 Bytes (in binary)
// 50 MB = 52428800 Bytes (in binary)
// The size that is more than 30 MB causes the long uploading (maybe, it's related to machine power).
const MAX_BLOB_BLOCK_SIZE = 20971520;

// The maximum file size that can be uploaded in a single request.
const MAX_FILE_SIZE_FOR_ENTIRE_UPLOAD = 52428800;

export const axiosClean = axios.create({
    transformRequest: [(data, headers) => {
        if (headers.common && headers.common.Authorization) {
            delete headers.common.Authorization;
        }
        
        return data
    }]
});

export function downloadFileRoot(url, fileName) {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function downloadFile(url, fileName, completion) {
    axiosClean({
        url: url,
        method: 'GET',
        responseType: 'blob',
    }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }).catch(error => {
        alert('There was an error downloading your video.');
    }).finally(() => {
        if (completion != null) {
            completion();
        }
    });
}

export function loadBlob(url, onProgress) {
    return new Promise((resolve, reject) => {
        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';
        xhr.onprogress = (e) => {
            if (!e.lengthComputable || typeof onProgress !== 'function') 
                return;

            onProgress(e.loaded / e.total);
        }
        xhr.onload = (e) => {
            if (e.srcElement.status === 200) {
                resolve(e.srcElement.response);
            } else {
                reject(e.srcElement.status);
            }
        };
        xhr.send();
    });
}

export const getBlocksUploadDataForLargeFile = (file, blobName, blockSize) => {
    let blocks = [];
    let offset = 0;
    let index = 0;
    const normalizedBlobName = blobName.replace('-', '');
    
    while (offset < file.size) {
        let payload = file.slice(offset, offset + blockSize);
        const blockId = `${('000' + (++index)).slice(-3)}${normalizedBlobName}`;

        blocks.push({
            id: base64Encode(blockId),
            payload: payload,
            size: payload.size,
            type: file.type
        });

        offset += blockSize;
    }

    let blocksUploadData = {
        blocks: blocks,
        targetBlockIndex: 0,
        totalFileSize: file.size,
        totalUploadedSize: 0
    };
  
    return blocksUploadData;
};

const uploadBlobBlocksList = (sasUrl, blocksUploadData, abortSignal) => {
    let elements = '';
    for (var i = 0; i < blocksUploadData.blocks.length; i++) {
        elements += `<Latest>${blocksUploadData.blocks[i].id}</Latest>`;
    }

    const body = `<?xml version="1.0" encoding="utf-8"?><BlockList>${elements}</BlockList>`;
    let headers = {
        'Content-Type': 'text/plain; charset=UTF-8',
        'x-ms-version': '2018-03-28',
    };

    return requestWithRetry(() => axiosClean.put(`${sasUrl}&comp=blocklist`, body, { headers: headers, signal: abortSignal }));
};

export function uploadEntireBlob(file, destinationUrl, onProgress, abortSignal) {
    return requestWithRetry(() => axiosClean.put(
        destinationUrl,
        file,
        {
            headers: {
                'Content-Type': file.type,
                'x-ms-blob-type': 'BlockBlob',
                'x-ms-version': '2018-03-28',
            },
            signal: abortSignal,
            onUploadProgress: (progress) => {
                if (typeof onProgress === 'function') {
                    onProgress(progress.loaded / progress.total * 100);
                }
            }
        }
    ));
}

export const uploadBlobBlocksRecursively = (sasUrl, blocksUploadData, onProgress, onComplete, onError, abortSignal) => {
    const currentBlock = blocksUploadData.blocks[blocksUploadData.targetBlockIndex];

    requestWithRetry(() => axiosClean.put(
        `${sasUrl}&comp=block&blockid=${currentBlock.id}`,
        currentBlock.payload,
        {
            headers: {
                'Content-Type': currentBlock.type,
                'x-ms-blob-type': 'BlockBlob',
                'x-ms-version': '2018-03-28',
            },
            signal: abortSignal,
            onUploadProgress: (progress) => {
                // Calculate the total uploading percentage of all the blocks.
                // On a retry the block restarts from zero, so the percentage re-bases to the completed blocks.
                onProgress?.((blocksUploadData.totalUploadedSize + progress.loaded) / blocksUploadData.totalFileSize * 100);
            }
        }
    )).then(() => {
        blocksUploadData.totalUploadedSize += currentBlock.size;
        const hasMoreBlocks = Boolean(blocksUploadData.blocks[blocksUploadData.targetBlockIndex + 1]);

        if (hasMoreBlocks) {
            // Upload the next block.
            blocksUploadData.targetBlockIndex++;
            uploadBlobBlocksRecursively(sasUrl, blocksUploadData, onProgress, onComplete, onError, abortSignal);
        } else {
            // Upload the list of blocks ids. The blob uploading is done here.
            uploadBlobBlocksList(sasUrl, blocksUploadData, abortSignal)
                .then(() => onComplete())
                .catch(error => onError(error));
        }
    }).catch(error => {
        onError(error);
    });
};

export function uploadFileToBlobStorage(file, sasUrl, onUploadProgress, abortSignal) {
    return new Promise((resolve, reject) => {
        const onUploadComplete = () => resolve();
        const onUploadError = (error) => reject(error);

        if (file.size <= MAX_FILE_SIZE_FOR_ENTIRE_UPLOAD) {
            console.log("Uploading file (Entire)");
            // Upload the normal file with a single request.
            uploadEntireBlob(file, sasUrl, onUploadProgress, abortSignal)
                .then(onUploadComplete)
                .catch(onUploadError);

            return;
        }

        // Upload the large file with the chunks.
        console.log("Uploading file (Chunks)");
        const blobName = generateGuid();
        const blocksUploadData = getBlocksUploadDataForLargeFile(file, blobName, MAX_BLOB_BLOCK_SIZE);
        uploadBlobBlocksRecursively(sasUrl, blocksUploadData, onUploadProgress, onUploadComplete, onUploadError, abortSignal);
    });
}

export const extractAudioDuration = async (extension, data) => {
    // The data parameter can be a file (from file input), blob or a url.
    // Extension should be like mp3, aac, wav, etc.
    
    const isAccFormat = extension === 'aac';
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    const isUrlInput = typeof data === 'string';
    const dataUrl = isUrlInput ? data : URL.createObjectURL(data);

    const removeObjectUrls = () => {
        if (!isUrlInput)
            URL.revokeObjectURL(dataUrl);
    };

    // Can not get duration of aac format by audio element (We have to use AudioContext).  
    // When it's Firefox we have to use audio context to avoid some issues with audio duration.
    if (isAccFormat || isFirefox) {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const arrayBuffer = await fetch(dataUrl).then(response => response.arrayBuffer());
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        removeObjectUrls();

        // Validate the audio duration and return result.
        if (!audioBuffer.duration)
            throw new Error(`Audio duration is empty for input file!`);
        return audioBuffer.duration;
    }

    // Default duration extraction by audio element.
    const audio = new Audio();
    audio.src = dataUrl;
    audio.crossOrigin = "anonymous";
    return new Promise((resolve, reject) => {
        audio.addEventListener('loadedmetadata', () => {
            audio.remove();
            removeObjectUrls();

            // Validate the audio duration and return result.
            if (!audio.duration) {
                reject(`Audio duration is empty for input file!`);
                return;
            }
            resolve(audio.duration);
        });
        audio.addEventListener('error', () => reject(`Can't detect the audio duration for input file!`));
    });
}

export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
};

export function blobToBase64URL(blob, callback) {
    var reader = new FileReader();
    reader.readAsDataURL(blob); 
    reader.onloadend = function() {
        var base64data = reader.result;                
        callback(base64data);
    }
}

export function base64ToBlob(base64, contentType='', sliceSize=512) {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, {type: contentType});
    return blob;
}

export function dataURLtoBlob(dataURL) {
    // Convert base64 to raw binary data held in a string
    var byteString = atob(dataURL.split(',')[1]);
  
    // Separate out the mime component
    var mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0]
  
    // Write the bytes of the string to an ArrayBuffer
    var ab = new ArrayBuffer(byteString.length);
  
    // Create a view into the buffer
    var ia = new Uint8Array(ab);
  
    // Set the bytes of the buffer to the correct values
    for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
  
    // Write the ArrayBuffer to a blob, and you're done
    var blob = new Blob([ab], {type: mimeString});
    return blob;
  }

export function readBase64FromURL(url, callback) {
    var request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.responseType = 'blob';

    request.onload = function() {
        var reader = new FileReader();
        reader.readAsDataURL(request.response);
        reader.onload = (e) => callback(e.target.result);
    };

    request.send();
}

export function readBlobFromURL(url, onSuccess, onError, abortController) {
    let fetchOptions = {};

    if (abortController && abortController.signal) 
        fetchOptions.signal = abortController.signal;
    
    fetch(url, fetchOptions)
        .then(response => response.blob())
        .then(blob => onSuccess(new Blob([blob], { type: 'video/mp4' })))
        .catch((error) => {
            if (onError && typeof onError === 'function') {
                onError(error);
            }    
        });
}

export async function createBlobFromUrl(url, abortController) {
    let fetchOptions = {
        responseType: 'blob',
        timeout: 60000,
    };
    const maxRetries = 3;
    const retryDelay = 1000;

    // Add abort controller if it's provided
    if (abortController && abortController.signal)
        fetchOptions.signal = abortController.signal;
    
    // Implement retry logic
    let attempt = 0;
    let lastError = null;
    
    while (attempt < maxRetries) {
        try {
            const { data: blob } = await axiosClean.get(url, fetchOptions);
            return blob;
        } catch (error) {
            lastError = error;
            
            // Don't retry if the request was aborted or if it's a 4xx error (client error)
            if (axios.isCancel(error) || (error.response && error.response.status >= 400 && error.response.status < 500))
                throw error;
            
            // Wait before retrying
            attempt++;
            console.warn(`Create blob from URL attempt ${attempt} failed, retrying in ${retryDelay}ms...`, error);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
    }
    
    // If we've exhausted all retries, throw the last error
    console.error(`Failed to create blob from URL after ${maxRetries} attempts:`, url, lastError);
    throw lastError;
}

export function convertToMegabytes(fileSizeInBytes) {
    let fileSizeInMegabytes = fileSizeInBytes / (1024 * 1024);
    fileSizeInMegabytes = fileSizeInMegabytes.toFixed(2);
    return parseFloat(fileSizeInMegabytes);
}

export const getLocalUrl = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = function(event) {
            const url = URL.createObjectURL(new Blob([event.target.result], { type: file.type }));
            resolve(url);
        };
        reader.onerror = function(event) {
            reject("Error reading file: " + event);
        };
        reader.readAsArrayBuffer(file);
    });
};

export const validateImageResolution = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
        if (!file || !maxWidth || !maxHeight)
            reject('Missing required parameters');

        let reader = new FileReader();

        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target.result;

            img.onload = function () {
                const imageWidth = img.width;
                const imageHeight = img.height;

                if (imageWidth > maxWidth && imageHeight > maxHeight) {
                    reject();
                } else {
                    resolve();
                }
            };

            img.onerror = function () {
                reject('Failed to load image');
            };
        };

        reader.onerror = function () {
            reject('Failed to read file');
        };

        reader.readAsDataURL(file);
    });
}